import React, { forwardRef, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import _ from "lodash";
import { Form } from "antd";
import GenericMappingForm from '../GenericMappingForm';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';

const VdooMappingForm = forwardRef(function VdooMappingForm({disableProjectFields = false, ...props}, ref) {
  const featureFlags = useFeatureFlag();
  const IntRef = useRef({});
  const [mappedProjectList] = useState(() => {
  
    if (props.record) {
      const { artifactId, artifactName, version, versionId } = props.record;
      const versionIdArray = _.isEmpty(versionId) ? [] : versionId.split(',');
      const versionArray = _.isEmpty(version) ? [] : version.split(',');
      return {
        artifact: artifactId ? [{ id: artifactId, name: artifactName }] : [],
        version: versionIdArray.map((id, index) => ({ id, name: versionArray[index] }))
      }
    }
    return { artifact: [], version: [] };
  })

  const [options, setOptions] = useState(mappedProjectList.artifact);
  const [versions, setVersions] = useState(mappedProjectList.version);
  const [form] = Form.useForm();

  const setOptionsFromPagination = (option, listkey) => {
    if(listkey === "version"){
      setVersions(prev => _.uniqBy([...prev,...option], 'id'));
    } else {
      setOptions(prev => _.uniqBy([...prev,...option], 'id'));
    }
  };

  const fetchProjects = useCallback((key, params) =>{
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,...params });
      promise.then((response) => {
        /**
         * For mapping artifact and version
         * for version if artifact is already mapped then only add versions to the list
         **/ 
        if(mappedProjectList[key].length > 0 && !_.isEmpty(response.projects)){
          if(key === "artifact" || (key === "version" && !_.isEmpty(mappedProjectList['artifact']) && mappedProjectList['artifact'][0].id === params.project)){
            response.projects = [...response.projects, ...mappedProjectList[key]];
          }
        }
        const mappedProjectListKey = key === "artifact" ? "artifact" : "version";
        setOptionsFromPagination(response.projects, mappedProjectListKey)
      });
      return { promise, cancel };
  },[props.loginConfigId,props.toolName, mappedProjectList])

  const fetchArtifact = useCallback((params) => {
      return fetchProjects("artifact", params)
  }, [fetchProjects]);

  const fetchVersion = useCallback((params) => {
    return fetchProjects("version", params)
}, [fetchProjects]);

  const getExtraFieldsData = useCallback((config) => {
    const { artifactId , versionId} = config;
    const artifactName = options.find(artifact => artifact.id === artifactId)?.name
    const data = versions.reduce((acc,cv) =>{
      const { id, name } = cv;
      if(versionId?.includes(id)){
        acc.id.push(id);
        acc.name.push(name);
      }
      return acc;
    },{id:[],name:[]})
  
    return ({
      artifactId, artifactName,
      version: data.name.join(","),
      versionId: data.id.join(","),
    });
  }, [options, versions]);

   const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { versionId } = config
      return ({
        versionId: _.isEmpty(versionId) ? [] : versionId.split(','),
      });
    }, []);

   const onValuesChange = useCallback((value, field) => {
      const { name } = field || {};
      const configurations = form.getFieldValue("configurations");
      let updatedConfiguration = [
          ...configurations,
      ];
      updatedConfiguration[name] = {
          ...updatedConfiguration[name],
          versionId: undefined,
      }
      form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form]);

  const extraFields = useMemo(() => ([
    {
      name: 'artifactId',
      colSpan: '12',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Artifact Name',
      disabled: disableProjectFields,
      required: true,
      props: {
          api: fetchArtifact,
          allowClear: true,
          onChange: onValuesChange,
          autoFocus: !disableProjectFields,
      }
    },
    {
      name: 'versionId',
      colSpan: 12,
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      label: 'Version',
      disabled: disableProjectFields,
      props: {
          setExtraProps: (fieldProps, currentField) => {
              const configurations = form.getFieldValue("configurations");
              const currentConfiguration = configurations[currentField.name] || {};
              const artifactId = currentConfiguration?.artifactId;
              if (artifactId && !IntRef.current[artifactId]) {
                  IntRef.current[artifactId] = { project: artifactId }
              }
          
              return {
                  mode: "multiple",
                  api: artifactId && fetchVersion,
                  allowClear: true,
                  apiParams: IntRef.current[artifactId],
                  resetOptionsProp: IntRef.current[artifactId],
              }
          },
          placeholder: "Leave blank to select all versions",
      }
  }
  ]), [fetchArtifact, fetchVersion, onValuesChange, form, disableProjectFields]);

  return (
    <GenericMappingForm
      title={<>Map {props.toolName} Artifacts</>}
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      {...props}
    />
  )
})

export default VdooMappingForm;
